import { FC, MouseEvent, RefCallback, useCallback } from "react";
import classNames from "classnames";
import { ItemProps as ExternalItemProps } from "../Item";
import { getHtmlPropsFromBaseProps } from "../../../utilities/getHtmlPropsFromBaseProps";
import { Tooltip } from "../../Tooltip";
import styles from "./styles.module.scss";
import { MenuCloseEventHandler } from "../MenuCloseEventHandler";

/**
 * The `Item` component properties.
 */

export interface ItemProps extends ExternalItemProps {
  /**
   * Called when the item has been clicked and is closing the menu.
   */
  readonly onClose?: MenuCloseEventHandler;

  /**
   * The unique id for the menu item element.
   */
  readonly id?: string;

  /**
   * The aria-label for the menu item element.
   */
  readonly "aria-label"?: string;
}

/**
 * The renderable menu item.
 */
export const Item: FC<ItemProps> = (props) => {
  const { disabled = false, type = "normal", onClick, onClose, tooltip, children, "aria-label": ariaLabel, id } = props;

  const itemId = id || `menuitemid`;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Item, "item", type, { disabled }, "option", "content"],
    testIdPrefix: "menu-item",
  });

  const clickHandler = useCallback(
    (ev: MouseEvent) => {
      ev.preventDefault();
      if (disabled) return;

      onClick?.();
      onClose?.();
    },
    [disabled, onClick],
  );

  const content = (ref?: RefCallback<HTMLButtonElement>) => (
    <>
      {disabled ? (
        <span
          {...baseHtmlProps}
          id={itemId}
          role="menuitem"
          aria-disabled="true"
          tabIndex={-1}
          className={classNames(baseHtmlProps.className, styles.DisabledItem)}
        >
          {children}
        </span>
      ) : (
        <button
          {...baseHtmlProps}
          id={itemId}
          ref={ref}
          type="button"
          role="menuitem"
          tabIndex={0}
          onClick={clickHandler}
          aria-label={ariaLabel || undefined}
        >
          {children}
        </button>
      )}
    </>
  );

  return (
    <>
      {tooltip && <Tooltip content={tooltip}>{content}</Tooltip>}
      {!tooltip && content()}
    </>
  );
};
